/**
 * Background refresh signal.
 *
 * Centralised event bus that pages subscribe to. We emit a `rc:refresh`
 * window event whenever something likely changed in the background:
 *
 *  - the browser tab becomes visible again (user reopens the PWA)
 *  - the service worker receives a push notification
 *  - the network reconnects after going offline
 *
 * Pages and components call `useBackgroundRefresh(handler)` to be invoked
 * on every signal. Handlers should be fast and idempotent (typically a
 * re-fetch). Throttled so the same tick can't fire it twice.
 */
import { useEffect, useRef } from "react";

const REFRESH_EVENT = "rc:refresh";
let lastFire = 0;
const THROTTLE_MS = 800;

export function emitBackgroundRefresh(reason = "manual") {
  const now = Date.now();
  if (now - lastFire < THROTTLE_MS) return;
  lastFire = now;
  try { window.dispatchEvent(new CustomEvent(REFRESH_EVENT, { detail: { reason, ts: now } })); }
  catch { /* ignore */ }
}

/**
 * Register the global listeners (visibility, online, SW messages).
 * Call once near the app root.
 */
export function installBackgroundRefresh() {
  if (typeof window === "undefined") return;
  if (window.__rcRefreshInstalled) return;
  window.__rcRefreshInstalled = true;

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") emitBackgroundRefresh("visible");
  });
  window.addEventListener("focus", () => emitBackgroundRefresh("focus"));
  window.addEventListener("online", () => emitBackgroundRefresh("online"));

  // Service worker (push events) → broadcast to clients
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.addEventListener("message", (event) => {
      if (event?.data?.type === "push-received") emitBackgroundRefresh("push");
    });
  }
}

/**
 * Subscribe a handler. Called on every refresh signal while mounted.
 */
export function useBackgroundRefresh(handler) {
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => {
    const fn = (e) => { try { ref.current?.(e?.detail); } catch { /* ignore */ } };
    window.addEventListener(REFRESH_EVENT, fn);
    return () => window.removeEventListener(REFRESH_EVENT, fn);
  }, []);
}
